/**
 * Cloudflare Turnstile, shown only when Mojo Up AI Cloud's discovery names a site key (sign-up checks
 * are on): on email sign-in and on creating a team. The widget's answer goes with the request as
 * `turnstileToken`; an answer is spent once, so the caller remounts the widget (a new `key`) after
 * each try.
 */

import { useEffect, useRef } from 'react';

import { routes } from '../../api/client.ts';
import { useLoad } from '../../hooks.ts';

type TurnstileApi = {
  render(el: HTMLElement, opts: { sitekey: string; theme: 'light' | 'dark'; size?: 'flexible'; callback: (token: string) => void; 'expired-callback': () => void; 'error-callback': () => void }): string;
  remove(id: string): void;
};
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading: Promise<TurnstileApi> | undefined;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not load')));
    s.onerror = () => {
      loading = undefined;
      reject(new Error('Turnstile did not load'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

/** The site key when Cloud asks for the check; undefined while unknown or when it does not. */
export function useTurnstileSiteKey(): string | undefined {
  return useLoad(() => routes.discovery(), []).data?.turnstileSiteKey;
}

/** The check itself: calls `onToken` with an answer, or undefined when it expires or fails. */
export function Turnstile({ siteKey, onToken }: { siteKey: string; onToken: (token: string | undefined) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const report = useRef(onToken);
  useEffect(() => {
    report.current = onToken;
  }, [onToken]);
  useEffect(() => {
    let id: string | undefined;
    let api: TurnstileApi | undefined;
    let gone = false;
    void loadTurnstile()
      .then((t) => {
        if (gone || !box.current) return;
        api = t;
        id = t.render(box.current, {
          sitekey: siteKey,
          theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
          size: 'flexible',
          callback: (token) => report.current(token),
          'expired-callback': () => report.current(undefined),
          'error-callback': () => report.current(undefined),
        });
      })
      .catch(() => report.current(undefined));
    return () => {
      gone = true;
      if (api && id) api.remove(id);
    };
  }, [siteKey]);
  return <div ref={box} className="min-h-[65px] w-full" aria-label="Check that you are a person" />;
}
